// ---------------------------------------------------------------------------
// The join between two phase screens.
//
// Phases used to hard-cut: the lobby was replaced by a board between one frame
// and the next, with nothing to say the game had moved. This crosses them over
// instead, briefly, and it is deliberately not more than that — a round is
// thirty seconds long and a transition that has to be waited out is a
// transition that gets in the way.
//
// Three rules it is built to.
//
//   1. NO LAYOUT SHIFT. The screen coming in is the only one in flow. The one
//      going out is lifted to `absolute inset-0` over it for the length of the
//      fade, so the two never both take up space and nothing below either of
//      them moves.
//   2. The outgoing screen is untouchable. It is `inert`, `aria-hidden` and
//      `pointer-events-none` while it leaves, so it cannot be tapped, focused
//      or read out as a second copy of the room. What it does keep for those
//      ~160ms is any window-level key listener it registered; the server is
//      authoritative on phase and refuses an action for a phase it has left,
//      which is what makes that survivable.
//   3. Reduced motion is the stylesheet's business, not this file's. Both
//      halves fade with a CSS transition, and index.css clamps every transition
//      on the page to 0.01ms under either the OS setting or the app's own — so
//      this becomes the hard cut it replaced, with no branch here to keep in
//      step.
//
// OPTING OUT. A boundary that another animation owns must not also be dissolved
// through, or the two run over each other. Name the pair in `CROSSFADE_EXEMPT`
// and this steps aside for it entirely.
//
// TWO COMPONENTS, ONE MECHANISM. `Crossfade` is the join itself and knows
// nothing about a game; `PhaseCrossfade` is that join with the phase table's
// rules on top. The room page uses the plain one for its own boundary — the
// seat notice giving way to the room — and it must stay the same movement at
// the same length, because a player crossing from the home screen into a lobby
// and then from a lobby into a board should not be able to tell that two
// different files drew it.
// ---------------------------------------------------------------------------

import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { GamePhase } from '@countdown/shared';

/**
 * Short enough to read as one movement rather than as a wait.
 *
 * Exported because the home screen dissolves ITSELF over this window on the way
 * into a room, and then waits it out before navigating.
 *
 * **It is 150 because every `duration-150` in this file and in `HomeScreen` is,
 * and it was 160.** Tailwind cannot import a constant, so this is one number in
 * two places and the two had drifted — the same shape as `TILE_FLIP_MS` and its
 * keyframes, which is the drift `motion.test.ts` exists to catch. The cost was
 * small and real: the home screen finished fading and then sat fully invisible
 * for ten more milliseconds before the navigation, adding blank to the middle of
 * a transition whose whole problem was the blank in the middle.
 *
 * `motion.test.ts` now pins the pair, so a future `duration-200` here fails
 * rather than quietly desynchronising the wait from the animation.
 */
export const FADE_MS = 150;

/**
 * Boundaries this must keep its hands off, as `from` → `to`.
 *
 * The letters board is carried from the selection screen to the play screen by
 * a shared-element animation on the tiles themselves. Fading the whole screen
 * underneath that would dissolve the very tiles it is moving.
 */
export const CROSSFADE_EXEMPT: ReadonlySet<string> = new Set<string>([
  crossfadeKey('letters-selection', 'letters-play'),
]);

/** The key a `from` → `to` pair is listed under. */
export function crossfadeKey(from: GamePhase, to: GamePhase): string {
  return `${from}>${to}`;
}

export interface PhaseCrossfadeProps {
  /** Changing this is what counts as a phase change. */
  phaseKey: GamePhase;
  /**
   * The screen for a phase.
   *
   * A function rather than children, because for the length of the fade this
   * has to render two of them. The one on its way out is asked for by name, so
   * the phase it belongs to is the only thing about it that is held back — it
   * still sees the room's current state, which is what keeps a fading screen a
   * screen rather than a stale photograph of one.
   */
  render: (phase: GamePhase) => ReactNode;
}

export function PhaseCrossfade({ phaseKey, render }: PhaseCrossfadeProps) {
  return (
    <Crossfade
      contentKey={phaseKey}
      exempt={(from, to) => CROSSFADE_EXEMPT.has(crossfadeKey(from as GamePhase, to as GamePhase))}
      render={(phase) => render(phase as GamePhase)}
    />
  );
}

export interface CrossfadeProps {
  /** Changing this is what counts as a change of content. */
  contentKey: string;
  /**
   * The content for a key.
   *
   * A function rather than children, because for the length of the fade this
   * has to render two of them. See the note on `PhaseCrossfadeProps.render`:
   * only the key is held back, never the state underneath it.
   */
  render: (key: string) => ReactNode;
  /** Answer true for a pair this must keep its hands off. */
  exempt?: (from: string, to: string) => boolean;
  /**
   * Fade the arriving content in even when nothing is going out — which
   * includes the very first content this ever draws.
   *
   * Off by default, and it has to be: a phase screen appearing inside a room
   * that is already on the screen has nothing to fade up from, and the first
   * frame of a room must not be a dark one. The room page turns it on because
   * it is the far half of a join that started somewhere else — the home screen
   * fades itself out, this page fades itself in, and neither can see the other
   * because the router has replaced one with the other by then.
   */
  fadeIn?: boolean;
  /**
   * The wrapper's own box. It has to establish the positioning context the
   * outgoing copy is lifted into, and it has to have a definite height, or a
   * child asking for `min-h-full` resolves against nothing. The default is what
   * a phase screen wants inside the room shell's flex column.
   */
  className?: string;
}

export function Crossfade({
  contentKey,
  render,
  exempt,
  fadeIn = false,
  className = 'relative min-h-0 flex-1',
}: CrossfadeProps) {
  const [shown, setShown] = useState<string>(contentKey);
  const [outgoing, setOutgoing] = useState<string | null>(null);

  // Adjusting state during the render that noticed the change, rather than from
  // an effect: an effect runs a frame after the commit, so the old screen would
  // be gone for that frame and the new one would arrive at zero opacity — a
  // flash of empty room, which is worse than the hard cut this replaces.
  if (shown !== contentKey) {
    setOutgoing(exempt?.(shown, contentKey) === true ? null : shown);
    setShown(contentKey);
  }

  useEffect(() => {
    if (outgoing === null) return;
    const timer = window.setTimeout(() => setOutgoing(null), FADE_MS);
    return () => window.clearTimeout(timer);
  }, [outgoing]);

  return (
    <div className={className}>
      {outgoing === null ? null : (
        <Leaving key={`leaving-${outgoing}`}>{render(outgoing)}</Leaving>
      )}
      <Arriving key={contentKey} faded={outgoing !== null || fadeIn}>
        {render(contentKey)}
      </Arriving>
    </div>
  );
}

/**
 * The screen going out, over the top of the one coming in.
 *
 * Mounted at full opacity and dropped on the next frame, because a class that
 * is already applied on the first frame has nothing to transition from.
 */
function Leaving({ children }: { children: ReactNode }) {
  const [gone, setGone] = useState(false);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setGone(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <div
      inert
      aria-hidden="true"
      data-phase-leaving=""
      className={`pointer-events-none absolute inset-0 overflow-hidden transition-opacity duration-150 ease-out ${
        gone ? 'opacity-0' : 'opacity-100'
      }`}
    >
      {children}
    </div>
  );
}

/**
 * The screen coming in. Nothing is layered on it once it has arrived: it holds
 * `opacity-100`, which is `opacity: 1` and therefore not a stacking context, so
 * a screen's own layering behaves exactly as it does with no wrapper at all.
 */
function Arriving({ faded, children }: { faded: boolean; children: ReactNode }) {
  const [settled, setSettled] = useState(!faded);

  useEffect(() => {
    if (settled) return;
    const frame = requestAnimationFrame(() => setSettled(true));
    return () => cancelAnimationFrame(frame);
  }, [settled]);

  return (
    <div
      data-phase-screen=""
      className={`h-full transition-opacity duration-150 ease-out ${
        settled ? 'opacity-100' : 'opacity-0'
      }`}
    >
      {children}
    </div>
  );
}
